import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { Plus, Trash2, Save, X, BookOpen, Edit3 } from 'lucide-react';

const EMPTY = {
  name: '',
  category: '',
  description: '',
  features: [],
  materials: [],
  fit_notes: '',
  size_notes: '',
  care_instructions: [],
  heritage_details: [],
};

const linesToArr = (t) => (t || '').split('\n').map(s => s.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join('\n');

function TemplateForm({ initial, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [featuresText, setFeaturesText] = useState(arrToLines(initial?.features));
  const [materialsText, setMaterialsText] = useState(arrToLines(initial?.materials));
  const [careText, setCareText] = useState(arrToLines(initial?.care_instructions));
  const [heritageText, setHeritageText] = useState(arrToLines(initial?.heritage_details));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !initial?.id;

  useEffect(() => {
    const src = initial || EMPTY;
    setForm(src);
    setFeaturesText(arrToLines(src.features));
    setMaterialsText(arrToLines(src.materials));
    setCareText(arrToLines(src.care_instructions));
    setHeritageText(arrToLines(src.heritage_details));
    setError('');
  }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await onSave({
        ...form,
        features: linesToArr(featuresText),
        materials: linesToArr(materialsText),
        care_instructions: linesToArr(careText),
        heritage_details: linesToArr(heritageText),
      }, isNew);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Panel className="p-6">
      <form onSubmit={submit} className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-eyebrow">{isNew ? 'New template' : 'Editing template'}</div>
            <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
              {form.name || 'Untitled template'}
            </div>
          </div>
          {onCancel && (
            <button type="button" className="h-btn-outline text-xs" onClick={onCancel}>
              <X size={14} className="mr-1.5" />Close
            </button>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="h-label">Template name</label>
            <input className="h-input" required value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="e.g. Signature Hoodie base" data-testid="tpl-name" />
          </div>
          <div>
            <label className="h-label">Category</label>
            <input className="h-input" value={form.category || ''}
                   onChange={(e) => setForm({ ...form, category: e.target.value })}
                   placeholder="Hoodie, Pants, Half Zip..." data-testid="tpl-category" />
          </div>
        </div>

        <div>
          <label className="h-label">Description</label>
          <textarea className="h-input" rows={5} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="tpl-description" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="h-label">Features (one per line)</label>
            <textarea className="h-input" rows={6} value={featuresText}
                      onChange={(e) => setFeaturesText(e.target.value)}
                      data-testid="tpl-features" />
          </div>
          <div>
            <label className="h-label">Materials (one per line)</label>
            <textarea className="h-input" rows={6} value={materialsText}
                      onChange={(e) => setMaterialsText(e.target.value)}
                      data-testid="tpl-materials" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="h-label">Fit notes</label>
            <textarea className="h-input" rows={3} value={form.fit_notes}
                      onChange={(e) => setForm({ ...form, fit_notes: e.target.value })} />
          </div>
          <div>
            <label className="h-label">Size guide notes</label>
            <textarea className="h-input" rows={3} value={form.size_notes}
                      onChange={(e) => setForm({ ...form, size_notes: e.target.value })} />
          </div>
          <div>
            <label className="h-label">Care instructions (one per line)</label>
            <textarea className="h-input" rows={5} value={careText}
                      onChange={(e) => setCareText(e.target.value)} />
          </div>
          <div>
            <label className="h-label">Heritage details (one per line)</label>
            <textarea className="h-input" rows={5} value={heritageText}
                      onChange={(e) => setHeritageText(e.target.value)} />
          </div>
        </div>

        {error && <div className="text-sm" style={{ color: '#9A2C2C' }}>{error}</div>}

        <div className="flex flex-wrap gap-2 justify-end">
          {onDelete && !isNew && (
            <button type="button" className="h-btn-outline text-xs" onClick={() => onDelete(initial.id)}
                    data-testid="tpl-delete">
              <Trash2 size={14} className="mr-1.5" /> Delete
            </button>
          )}
          <button type="submit" className="h-btn" disabled={saving} data-testid="tpl-save">
            <Save size={14} className="mr-1.5" /> {saving ? 'Saving...' : 'Save template'}
          </button>
        </div>
      </form>
    </Panel>
  );
}

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/admin/description-templates');
    setTemplates(data.templates || []);
  };
  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2200); };

  const save = async (tpl, isNew) => {
    if (isNew) {
      await api.post('/admin/description-templates', tpl);
      showToast('Template created');
    } else {
      await api.put(`/admin/description-templates/${tpl.id}`, tpl);
      showToast('Template saved');
    }
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await api.delete(`/admin/description-templates/${id}`);
    showToast('Template deleted');
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-6">
      <Panel className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="h-eyebrow">Products</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Description Templates</div>
          <div className="text-sm opacity-80 mt-2 max-w-2xl" style={{ color: 'var(--heritage-plum)' }}>
            Reusable description blocks for garment types like hoodies, pants and half zips. Load a template inside any product to instantly populate the description, features, materials, fit, care and heritage fields.
          </div>
        </div>
        <button className="h-btn" onClick={() => setEditing('new')} data-testid="tpl-new">
          <Plus size={14} className="mr-1.5" /> New template
        </button>
      </Panel>

      {toast && (
        <div className="fixed bottom-6 right-6 z-40 px-4 py-2 rounded-lg shadow-lg"
             style={{ background: 'var(--heritage-plum)', color: 'var(--heritage-cream)' }}
             data-testid="tpl-toast">
          {toast}
        </div>
      )}

      {editing === 'new' && (
        <TemplateForm initial={null} onSave={save} onCancel={() => setEditing(null)} />
      )}
      {editing && editing !== 'new' && (
        <TemplateForm initial={editing} onSave={save} onDelete={del} onCancel={() => setEditing(null)} />
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {templates.length === 0 && (
          <Panel className="md:col-span-2 p-8 text-center" style={{ color: 'var(--heritage-plum)' }}>
            <BookOpen size={28} className="mx-auto opacity-60" />
            <div className="h-serif text-xl mt-3">No templates yet</div>
            <div className="text-sm opacity-70 mt-1">Create your first reusable description block.</div>
          </Panel>
        )}
        {templates.map((tpl) => (
          <Panel key={tpl.id} className="p-5 flex flex-col gap-2" data-testid={`tpl-card-${tpl.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="h-eyebrow">{tpl.category || 'Uncategorised'}</div>
                <div className="h-serif text-xl leading-tight mt-1" style={{ color: 'var(--heritage-plum)' }}>{tpl.name}</div>
              </div>
              <button className="h-btn text-xs" onClick={() => setEditing(tpl)} data-testid={`tpl-edit-${tpl.id}`}>
                <Edit3 size={12} className="mr-1.5" /> Edit
              </button>
            </div>
            {tpl.description && (
              <p className="text-xs opacity-80 line-clamp-3" style={{ color: 'var(--heritage-plum)' }}>{tpl.description}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-1">
              <span className="h-chip">{tpl.features?.length || 0} features</span>
              <span className="h-chip">{tpl.materials?.length || 0} materials</span>
              <span className="h-chip">{tpl.care_instructions?.length || 0} care</span>
              <span className="h-chip">{tpl.heritage_details?.length || 0} heritage</span>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}
